import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, Clock, ListPlus, Loader2, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { createCostCenter, dodajDoSlownika, listCostCenters, type CostCenterRow, type KodZKart } from '@/lib/cost-centers-api';
import { nazwyMpkHr } from '@/lib/hrplus-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { NiezgodneCentra } from '@/components/NiezgodneCentra';
import { UsuniecieCentrum } from '@/components/UsuniecieCentrum';

/**
 * CENTRA KOSZTOWE — ekran o JEDNEJ rzeczy, której nie widać nigdzie indziej: SPS trzyma centrum
 * kosztowe w DWÓCH postaciach naraz i stempluje na wydaniu OBIE.
 *
 *   `Users.COSTCENTER`            → goły napis przy pracowniku  → `TransactionLog.COSTCENTER`
 *   `UserCostCenterMembership`    → przynależność do encji      → `TransactionLog.USERCOSTCENTER`
 *
 * ⚠️ DOPÓKI SIĘ RÓŻNIĄ, DWA RAPORTY „Z SPS" DAJĄ DWIE RÓŻNE ODPOWIEDZI NA TO SAMO PYTANIE —
 * i żaden z nich nie jest błędny, bo każdy czyta inną kolumnę. Tego nie da się zauważyć
 * z żadnego innego ekranu, więc liczba rozjazdu stoi tu na wierzchu, a nie w zwiniętej sekcji.
 *
 * ⚠️ KLUCZE POTWIERDZONE 29.09.2026 z `sys.indexes` na pięciu bazach (`sps-columns.ts`, status
 * `confirmed`) — dawne potwierdzenie „klucz ZAŁOŻONY" zdjęte z okna 14.10.2026 na prośbę usera.
 */

const FormSchema = z.object({
  /** `UserCostCenters.USERCOSTCENTER` (50) — klucz naturalny, TEKST nadawany przez człowieka. */
  code: z
    .string()
    .trim()
    .min(1, 'Podaj kod centrum')
    .max(50, 'Najwyżej 50 znaków — SPS obciąłby dłuższy bez ostrzeżenia'),
  name: z.string().trim().min(1, 'Podaj nazwę').max(50),
  alias: z.string().trim().max(50).optional(),
});

type FormValues = z.infer<typeof FormSchema>;

function CostCenterModal({
  groupId,
  istniejace,
  onClose,
  onSaved,
}: {
  groupId: string;
  istniejace: CostCenterRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { code: '', name: '', alias: '' },
  });

  /**
   * Kolizja kodu sprawdzana LOKALNIE, bez wielkości liter.
   *
   * Kolacja SPS nie rozróżnia wielkości liter, więc 'PROD' i 'prod' to dla bazy klienta JEDEN
   * wiersz. Osobnego endpointu nie ma, ale lista centrów jest krótka i cała jest tutaj —
   * sprawdzenie po stronie serwera i tak zrobi bramka przy zapisie.
   */
  const kodWpisany = watch('code')?.trim() ?? '';
  const zajety = istniejace.find((c) => c.code.toLowerCase() === kodWpisany.toLowerCase());

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await createCostCenter(groupId, values.code, values.name, values.alias?.trim() || undefined);
      toast.success(t('cc.created'));
      onSaved();
      onClose();
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('cc.addTitle')} description={t('cc.codeHint')}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormGrid>
            <Field label={t('cc.code')} htmlFor="code" required error={errors.code?.message} hint={t('cc.codeHint')}>
              <Input id="code" autoComplete="off" autoFocus placeholder="np. PROD-01" {...register('code')} />
              {zajety && (
                <p className="mt-1 text-xs text-[var(--destructive)]">
                  {t('cc.codeTaken').replace('{name}', zajety.name)}
                </p>
              )}
            </Field>

            <Field label={t('cc.name')} htmlFor="name" required error={errors.name?.message}>
              <Input id="name" autoComplete="off" {...register('name')} />
            </Field>

            <Field label={t('cc.alias')} htmlFor="alias" error={errors.alias?.message} hint={t('cc.aliasHint')}>
              <Input id="alias" autoComplete="off" {...register('alias')} />
            </Field>

            <ModalError>{serverError}</ModalError>
          </FormGrid>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || Boolean(zajety)}>
              {isSubmitting && <Loader2 className="animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Wiersz listy: centrum ze słownika albo kod z kart spoza słownika (`spoza`, 13.10.2026). */
type Wiersz = CostCenterRow & { spoza: boolean };

/**
 * KODY Z KART DO SŁOWNIKA (13.10.2026) — jedno okno na jeden kod i na wszystkie naraz.
 *
 * ⚠️ NAZWA Z HR+, gdy wyspa ma integrację, a HR+ ją zna; inaczej sam kod. Pobranie z HR+ to pełna
 * lista ludzi, więc idzie dopiero przy otwarciu okna, a jego błąd (wyspa bez HR+) niczego nie blokuje.
 *
 * ⚠️ PRZYNALEŻNOŚCI NIE ZAKŁADAMY (decyzja usera): automat liczy przynależności osoby i przy dwóch
 * pyta o centrum przy każdym pobraniu. Sam wpis w słowniku na maszynie niczego nie zmienia.
 */
function DoSlownikaModal({
  groupId,
  kody,
  onClose,
  onSaved,
}: {
  groupId: string;
  kody: KodZKart[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [wpisane, setWpisane] = useState<Record<string, string>>({});
  const [zapis, setZapis] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const zHr = useQuery({ queryKey: ['hr-nazwy-mpk', groupId], queryFn: () => nazwyMpkHr(groupId), retry: false, staleTime: 5 * 60_000 });
  const nazwa = (code: string) => wpisane[code] ?? zHr.data?.nazwy[code.trim().toLowerCase()] ?? code.slice(0, 50);
  const zle = kody.filter((k) => {
    const n = nazwa(k.code).trim();
    return n === '' || n.length > 50;
  });

  const zapisz = async () => {
    setServerError(null);
    setZapis(true);
    try {
      const w = await dodajDoSlownika(
        groupId,
        kody.map((k) => ({ code: k.code, name: nazwa(k.code).trim() })),
      );
      toast.success(t('cc.doSlownika.dodano').replace('{n}', String(w.dodanych)));
      onSaved();
      onClose();
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setZapis(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('cc.doSlownika.tytul')} description={t('cc.doSlownika.opis')}>
        {zHr.isFetching && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" /> {t('cc.doSlownika.nazwyHr')}
          </p>
        )}
        <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('cc.code')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('cc.doSlownika.osob')}</th>
                <th className="px-3 py-2 font-medium">{t('cc.name')}</th>
              </tr>
            </thead>
            <tbody>
              {kody.map((k) => (
                <tr key={k.code} className="border-t border-border/60">
                  <td className="px-3 py-1.5 font-mono text-xs">{k.code}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{k.osob}</td>
                  <td className="px-3 py-1.5">
                    <Input
                      aria-label={`${t('cc.name')} ${k.code}`}
                      maxLength={50}
                      value={nazwa(k.code)}
                      onChange={(e) => setWpisane((w) => ({ ...w, [k.code]: e.target.value }))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ModalError>{serverError}</ModalError>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => void zapisz()} disabled={zapis || zle.length > 0}>
            {zapis && <Loader2 className="animate-spin" />}
            {t('cc.doSlownika.dodaj').replace('{n}', String(kody.length))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CostCenters() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { groupId, island } = useIslands();

  const [state, setState] = useState<DataTableState>({
    search: '',
    page: 1,
    pageSize: 50,
    sort: 'code',
    dir: 'asc',
  });
  const [dodawanie, setDodawanie] = useState(false);
  const [doSlownika, setDoSlownika] = useState<KodZKart[] | null>(null);
  /** Kod do usunięcia (03.11.2026) — ze słownika albo „z kart"; okno ze skutkami w `UsuniecieCentrum`. */
  const [doUsuniecia, setDoUsuniecia] = useState<string | null>(null);

  const dane = useQuery({
    queryKey: ['cost-centers', groupId, state.search],
    queryFn: () => listCostCenters(groupId!, state.search || undefined),
    enabled: Boolean(groupId),
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  const columns = useMemo<ColumnDef<Wiersz>[]>(
    () => [
      {
        id: 'code',
        header: t('cc.code'),
        meta: { exportValue: (r) => r.code },
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs">{row.original.code}</span>
            {row.original.spoza && (
              <Badge variant="neutral" title={t('cc.spozaHint')}>
                {t('cc.spoza')}
              </Badge>
            )}
            {row.original.pending && (
              <Badge variant="warning" title={t('cc.pendingHint')}>
                <Clock className="size-3" />
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'name',
        header: t('cc.name'),
        meta: { exportValue: (r) => r.name },
        cell: ({ row }) =>
          row.original.spoza ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="font-medium">{row.original.name}</span>
          ),
      },
      {
        id: 'alias',
        header: t('cc.alias'),
        enableSorting: false,
        meta: { exportValue: (r) => r.alias ?? '' },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.alias ?? '—'}</span>
        ),
      },
      {
        id: 'members',
        header: t('cc.members'),
        enableSorting: false,
        meta: { exportValue: (r) => r.members, align: 'right' },
        cell: ({ row }) => <span className="tabular-nums">{row.original.members}</span>,
      },
      {
        id: 'onUsersColumn',
        header: t('cc.onColumn'),
        enableSorting: false,
        meta: { exportValue: (r) => r.onUsersColumn, align: 'right' },
        /** Liczba prowadzi do tych ludzi — lista pracowników z filtrem centrum (13.10.2026). */
        cell: ({ row }) =>
          row.original.onUsersColumn > 0 ? (
            <Link
              to={`/pracownicy?centrum=${encodeURIComponent(row.original.code)}`}
              className="tabular-nums text-[var(--primary)] hover:underline"
            >
              {row.original.onUsersColumn}
            </Link>
          ) : (
            <span className="tabular-nums">0</span>
          ),
      },
      {
        id: 'rozjazd',
        header: t('cc.divergent'),
        enableSorting: false,
        meta: {
          exportValue: (r) => (r.members === 0 || r.members === r.onUsersColumn ? '' : String(r.onUsersColumn - r.members)),
          align: 'right',
        },
        /**
         * ⚠️ RÓŻNICA POKAZANA WPROST, NIE WYGŁADZONA. Obie liczby są prawdziwe i obie trafiają
         * na wydanie — to nie jest „jedna z nich jest błędna", tylko „raport odpowie inaczej
         * w zależności od tego, którą kolumnę przeczyta".
         */
        cell: ({ row }) => {
          const d = row.original.onUsersColumn - row.original.members;
          /**
           * Bez żadnej przynależności to zwykły stan „centrum tylko na kartach" — tak pracuje większość
           * klientów i tego nie zakładamy (13.10.2026), więc nie ma tu czego ostrzegać.
           */
          if (d === 0 || row.original.members === 0) return <span className="text-muted-foreground">—</span>;
          return (
            <Badge variant="warning" title={t('cc.divergentHint')}>
              {d > 0 ? `+${d}` : d}
            </Badge>
          );
        },
      },
      ...(isAdmin
        ? [
            {
              id: 'actions',
              header: '',
              enableSorting: false,
              cell: ({ row }) => (
                <span className="flex justify-end gap-1">
                  {row.original.spoza && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDoSlownika([{ code: row.original.code, osob: row.original.onUsersColumn }])}
                    >
                      {t('cc.doSlownika.przycisk')}
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    disabled={row.original.pending}
                    title={t('cc.usun.akcja')}
                    aria-label={`${t('cc.usun.akcja')} ${row.original.code}`}
                    onClick={() => setDoUsuniecia(row.original.code)}
                  >
                    <Trash2 />
                  </Button>
                </span>
              ),
            } satisfies ColumnDef<Wiersz>,
          ]
        : []),
    ],
    [t, isAdmin],
  );

  /**
   * ⚠️ SŁOWNIK I KODY Z KART W JEDNEJ LIŚCIE, sortowanej i stronicowanej TUTAJ — API oddaje całość
   * (centrów jest kilkadziesiąt), a tabela stronicuje ręcznie i bez przycięcia pokazywałaby wszystko.
   */
  const wiersze = useMemo<Wiersz[]>(() => {
    const d = dane.data;
    if (!d) return [];
    const wszystkie: Wiersz[] = [
      ...d.rows.map((r) => ({ ...r, spoza: false })),
      ...d.spozaSlownika.map((k) => ({
        id: `spoza:${k.code}`,
        code: k.code,
        name: '',
        alias: null,
        members: 0,
        onUsersColumn: k.osob,
        pending: false,
        spoza: true,
      })),
    ];
    const pole = state.sort === 'name' ? 'name' : 'code';
    const kierunek = state.dir === 'desc' ? -1 : 1;
    return wszystkie.sort((a, b) => kierunek * a[pole].localeCompare(b[pole], 'pl', { numeric: true }));
  }, [dane.data, state.sort, state.dir]);
  const strona = wiersze.slice((state.page - 1) * state.pageSize, state.page * state.pageSize);

  /**
   * ⚠️⚠️ HOOK NAD BRAMKĄ NA WYSPĘ, i to nie jest kosmetyka. `groupId` jest `null`, dopóki nie
   * wczyta się lista wysp, więc pierwszy render wychodzi wyżej — a hook postawiony pod tym
   * `return`em wykonywałby się dopiero w drugim renderze. React przerywa wtedy całe drzewo
   * („rendered more hooks than during the previous render") i zakładka gaśnie na BIAŁY EKRAN.
   * Dokładnie to wyłożyło zakładkę „Moduły" 02.09.2026; pilnuje tego `hooki-nad-returnami.spec.ts`.
   */
  const [niezgodniOtwarte, setNiezgodniOtwarte] = useState(false);

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  const s = dane.data?.summary;
  /** `doPoprawy` liczy serwer tą samą regułą co listę niezgodnych; wzór niżej tylko dla starszego API. */
  const rozjazd = s ? (s.doPoprawy ?? s.mismatch + s.multiple + s.onlyColumn + s.onlyMembership) : 0;
  const spoza = dane.data?.spozaSlownika ?? [];

  return (
    <>
      {/* Kafelki liczą CAŁĄ wyspę, niezależnie od szukajki — inaczej ten sam napis pokazywałby
          raz stan wyspy, a raz stan przefiltrowanego podzbioru. */}
      {s && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            { label: t('cc.kpi.centers'), value: s.centers },
            { label: t('cc.kpi.mismatch'), value: s.mismatch },
            { label: t('cc.kpi.multiple'), value: s.multiple },
            { label: t('cc.kpi.onlyColumn'), value: s.onlyColumn },
            { label: t('cc.kpi.onlyMembership'), value: s.onlyMembership },
            { label: t('cc.kpi.unknownCodes'), value: s.unknownCodes },
          ].map((k) => (
            <div key={k.label} className="rounded-lg border border-border bg-card p-3">
              <p className="text-xs text-muted-foreground">{k.label}</p>
              <p className="mt-0.5 text-xl font-semibold tabular-nums">{k.value}</p>
            </div>
          ))}
        </div>
      )}

      {/*
        ⚠️⚠️ ALARM MÓWI TERAZ LICZBĄ, NIE WYKŁADEM (zgłoszone 01.09.2026: „to jest mega nieczytelne —
        jak chcesz tu przekazać jakiś błąd, to konkretnie, bo ja znając vending to nie wiem za bardzo
        o co chodzi"). Poprzednia wersja tłumaczyła budowę SPS (dwie postacie, nazwy tabel i kolumn),
        a nie mówiła ANI ile osób dotyczy, ANI co z tego wynika.

        ⚠️ AKAPIT O DWÓCH POSTACIACH ZNIKNĄŁ CAŁKIEM: kafle nad tabelą nazywają każdy rodzaj rozjazdu
        osobno („Napis ≠ przynależność", „Tylko napis", „Tylko przynależność"), więc wykład o tym,
        skąd się biorą, powtarzał to samo dłużej.
      */}
      {s && rozjazd > 0 && (
        <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span className="flex-1">{t('cc.divergenceIntro').replace('{n}', String(rozjazd))}</span>
          <Button size="sm" variant="outline" onClick={() => setNiezgodniOtwarte(true)}>
            {t('cc.niezgodni.pokaz')}
          </Button>
        </div>
      )}

      <DataTable<Wiersz>
        title={t('nav.costCenters')}
        columns={columns}
        data={strona}
        total={wiersze.length}
        state={state}
        onStateChange={onStateChange}
        isLoading={dane.isPending}
        isFetching={dane.isFetching}
        onRefresh={() => void dane.refetch()}
        rowKey={(r) => r.id}
        exportFileName={`centra-kosztowe-${island?.code ?? 'wyspa'}`}
        actions={
          isAdmin ? (
            <>
              {spoza.length > 0 && (
                <Button variant="outline" onClick={() => setDoSlownika(spoza)}>
                  <ListPlus />
                  {t('cc.doSlownika.wszystkie').replace('{n}', String(spoza.length))}
                </Button>
              )}
              <Button onClick={() => setDodawanie(true)}>
                <Plus />
                {t('cc.add')}
              </Button>
            </>
          ) : null
        }
      />

      {doUsuniecia !== null && (
        <UsuniecieCentrum groupId={groupId} kod={doUsuniecia} onClose={() => setDoUsuniecia(null)} />
      )}

      {doSlownika && (
        <DoSlownikaModal
          groupId={groupId}
          kody={doSlownika}
          onClose={() => setDoSlownika(null)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['cost-centers'] })}
        />
      )}

      {dodawanie && (
        <CostCenterModal
          groupId={groupId}
          istniejace={dane.data?.rows ?? []}
          onClose={() => setDodawanie(false)}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['cost-centers'] })}
        />
      )}

      {/*
        ⚠️ LISTA NIEZGODNYCH — droga od liczby w alarmie do konkretnych ludzi (zgłoszone 01.09.2026).
        Poprawia się to na karcie pracownika, więc okno tylko wskazuje, do kogo iść.
      */}
      {niezgodniOtwarte && (
        <NiezgodneCentra groupId={groupId} onClose={() => setNiezgodniOtwarte(false)} />
      )}
    </>
  );
}
